import { useState } from 'react';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import DischargeChecklistProgress from 'components/shared/dischargeChecklist/DischargeChecklistProgress';
import Checkbox from 'components/shared/form/Checkbox';
import Menu from 'components/shared/menu';
import Tooltip from 'components/shared/Tooltip';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { simpleDateAtTime } from 'lib/date';
import {
  DischargeChecklistEntry,
  DischargeChecklistNote,
  DischargeChecklistState,
} from 'models/DischargeChecklistItem';
import LocationEpisode from 'models/LocationEpisode';
import ProjectedDischarge from 'models/reviews/ProjectedDischarge';
import { dischargeCalendarQueryKeys } from 'services/api/dischargeCalendar';
import {
  DischargeChecklistOperation,
  dischargeChecklistQueryKeys,
  updatePatientDischargeChecklistItem,
} from 'services/api/dischargeChecklists';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { validationErrorMessage } from 'services/api/validationErrors';
import { colors } from 'styles/theme/colors';
import { BodySmall, LabelBold } from 'styles/typography';
import AddNoteIcon from 'svg/AddNoteIcon';
import ChevronDown from 'svg/ChevronDown';
import CircleReturnArrowIcon from 'svg/CircleReturnArrowIcon';
import CircleXIconOutlined from 'svg/CircleXIconOutlined';
import EllipsisIcon from 'svg/EllipsisIcon';
import NoteIcon from 'svg/NoteIcon';
import ViewHistoryIcon from 'svg/ViewHistoryIcon';

import DischargeChecklistHistoryModal from './DischargeChecklistHistoryModal';
import DischargeChecklistNoteModal from './DischargeChecklistNoteModal';

type Props = {
  review: ProjectedDischarge;
  locationEpisode: LocationEpisode;
  // Checkboxes disabled and an item menu with only "View history": the user can't change this patient's
  // checklist, or nobody can any more (discharged, archived, an earlier stay).
  readOnly: boolean;
};

// The note tooltip shows the start of a long note; View history and Update Note show all of it.
export const NOTE_PREVIEW_LENGTH = 500;

const STATE_AFTER: Partial<Record<DischargeChecklistOperation, DischargeChecklistState>> = {
  [DischargeChecklistOperation.CHECK]: DischargeChecklistState.DONE,
  [DischargeChecklistOperation.UNCHECK]: DischargeChecklistState.OPEN,
  [DischargeChecklistOperation.MARK_NOT_NEEDED]: DischargeChecklistState.NOT_NEEDED,
  [DischargeChecklistOperation.MARK_NEEDED]: DischargeChecklistState.OPEN,
};

function NoteTooltip({ note }: { note: DischargeChecklistNote }) {
  const text = note.text.length > NOTE_PREVIEW_LENGTH ? `${note.text.slice(0, NOTE_PREVIEW_LENGTH)}…` : note.text;

  return (
    <NoteTooltipContent data-testid='discharge-checklist-note-tooltip'>
      <NoteTooltipText>{text}</NoteTooltipText>
      <NoteTooltipByline>
        Added by {note.createdByName} on {simpleDateAtTime(note.createdAt)}
      </NoteTooltipByline>
    </NoteTooltipContent>
  );
}

export default function DischargeChecklistSection({ review, locationEpisode, readOnly }: Props) {
  const [collapsed, setCollapsed] = useState(true);
  // The change being saved, shown straight away so a click lands visibly before the card refetches.
  const [saving, setSaving] = useState<{ itemId: string; state: DischargeChecklistState } | null>(null);
  const [noteFor, setNoteFor] = useState<DischargeChecklistEntry | null>(null);
  const [historyFor, setHistoryFor] = useState<DischargeChecklistEntry | null>(null);
  const queryClient = useQueryClient();

  const refetch = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: locationEpisodeQueryKeys.show({ id: locationEpisode.id }) }),
      queryClient.invalidateQueries({ queryKey: dischargeChecklistQueryKeys.historyBase }),
      // The calendar shows each patient's progress. Its day pages never go stale on their own.
      queryClient.invalidateQueries({ queryKey: dischargeCalendarQueryKeys.base }),
    ]);

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: updatePatientDischargeChecklistItem,
    errorToast: validationErrorMessage,
    // Both returned, so the save stays pending until the card shows the saved state, or after a
    // refusal (usually someone else changed the checklist first) what's actually there now.
    onSuccess: refetch,
    onError: refetch,
    onSettled: () => setSaving(null),
  });

  const checklist = review.dischargeChecklist;
  if (!review.hasDischargeChecklist || !checklist) return null;

  const change = (item: DischargeChecklistEntry, operation: DischargeChecklistOperation) => {
    // One change at a time. Ignored rather than drawn disabled, so the other boxes don't fade (and
    // the cursor doesn't turn to not-allowed) for the moment a save takes.
    if (isPending) return;
    const state = STATE_AFTER[operation];
    if (state) setSaving({ itemId: item.itemId, state });
    mutate({ reviewId: review.id, itemId: item.itemId, operation });
  };

  const saveNote = (item: DischargeChecklistEntry, noteText: string) =>
    mutate(
      { reviewId: review.id, itemId: item.itemId, operation: DischargeChecklistOperation.SET_NOTE, noteText },
      { onSuccess: () => setNoteFor(null) }
    );

  const stateOf = (item: DischargeChecklistEntry) => (saving?.itemId === item.itemId ? saving.state : item.state);

  return (
    <Section data-testid='discharge-checklist'>
      <Header type='button' aria-expanded={!collapsed} onClick={() => setCollapsed(!collapsed)}>
        <StyledChevron $collapsed={collapsed} />
        <LabelBold>Discharge Checklist</LabelBold>
        <Progress>
          <DischargeChecklistProgress done={checklist.done} total={checklist.total} barWidth={80} />
        </Progress>
      </Header>
      {!collapsed && (
        <Items>
          {checklist.items.map((item) => {
            const state = stateOf(item);
            const notNeeded = state === DischargeChecklistState.NOT_NEEDED;
            const done = state === DischargeChecklistState.DONE;

            return (
              <Item key={item.itemId} $faded={notNeeded} data-testid='discharge-checklist-item'>
                <Checkbox
                  checked={done}
                  overrideSetValue
                  size={16}
                  disabled={readOnly || notNeeded}
                  onChange={(checked) =>
                    change(item, checked ? DischargeChecklistOperation.CHECK : DischargeChecklistOperation.UNCHECK)
                  }
                  dataTestId={`discharge-checklist-checkbox-${item.itemId}`}
                />
                <ItemLabel $done={done}>{item.label}</ItemLabel>
                {/* A not needed item's note is kept, hidden until the item is marked needed again. */}
                {item.note && !notNeeded && (
                  <Tooltip text={<NoteTooltip note={item.note} />} maxWidth={320} position='bottom'>
                    <NoteIconWrapper
                      role='img'
                      aria-label={`Note on ${item.label}`}
                      data-testid='discharge-checklist-note-icon'>
                      <NoteIcon width={11} height={12} />
                    </NoteIconWrapper>
                  </Tooltip>
                )}
                <Menu $size='small'>
                  <Menu.Trigger>
                    <MenuButton
                      type='button'
                      aria-label={`Actions for ${item.label}`}
                      data-testid='discharge-checklist-item-menu'>
                      <EllipsisIcon width={12} height={12} />
                    </MenuButton>
                  </Menu.Trigger>
                  <Menu.Content position='right' style={{ padding: 0, minWidth: 220 }}>
                    {/* Not needed items can't take a note. */}
                    <Menu.Item visible={!readOnly && !notNeeded} onClick={() => setNoteFor(item)}>
                      <Menu.Icon>
                        <AddNoteIcon width={16} height={14} />
                      </Menu.Icon>
                      {item.note ? 'Update note' : 'Add note'}
                    </Menu.Item>
                    <Menu.Item
                      visible={!readOnly && !notNeeded}
                      onClick={() => change(item, DischargeChecklistOperation.MARK_NOT_NEEDED)}>
                      <Menu.Icon>
                        <CircleXIconOutlined width={16} height={16} />
                      </Menu.Icon>
                      Mark as not needed
                    </Menu.Item>
                    <Menu.Item
                      visible={!readOnly && notNeeded}
                      onClick={() => change(item, DischargeChecklistOperation.MARK_NEEDED)}>
                      <Menu.Icon>
                        <CircleReturnArrowIcon width={16} height={16} />
                      </Menu.Icon>
                      Mark as needed
                    </Menu.Item>
                    <Menu.Item onClick={() => setHistoryFor(item)}>
                      <Menu.Icon>
                        <ViewHistoryIcon />
                      </Menu.Icon>
                      View history
                    </Menu.Item>
                  </Menu.Content>
                </Menu>
              </Item>
            );
          })}
        </Items>
      )}
      {noteFor && (
        <DischargeChecklistNoteModal
          item={noteFor}
          saving={isPending}
          onSubmit={(text) => saveNote(noteFor, text)}
          onClose={() => setNoteFor(null)}
        />
      )}
      {historyFor && (
        <DischargeChecklistHistoryModal reviewId={review.id} item={historyFor} onClose={() => setHistoryFor(null)} />
      )}
    </Section>
  );
}

const Section = styled.div`
  padding: 24px;
  border-top: 1px solid var(--black-10);
`;

const Header = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;

  label {
    cursor: pointer;
  }
`;

const StyledChevron = styled(ChevronDown)<{ $collapsed: boolean }>`
  transform: ${({ $collapsed }) => ($collapsed ? 'rotate(90deg)' : 'rotate(180deg)')};
  transform-origin: center;
  transition: transform 0.2s ease;
  width: 8px;
  height: 8px;
  flex: 0 0 auto;

  path {
    fill: ${colors.black100};
    stroke: ${colors.black100};
    stroke-width: 0.5px;
  }
`;

const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const Items = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li<{ $faded: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  opacity: ${({ $faded }) => ($faded ? 0.5 : 1)};
`;

const ItemLabel = styled(BodySmall)<{ $done: boolean }>`
  flex: 1;
  margin: 0;
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const NoteIconWrapper = styled.span`
  display: flex;
  align-items: center;
  padding: 4px;
  cursor: default;
`;

const NoteTooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const NoteTooltipText = styled.span`
  font-size: 14px;
  line-height: 20px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const NoteTooltipByline = styled.span`
  font-size: 11px;
  color: ${colors.white50};
`;
